/**
 * Pillar 6 — Foreman Roll-Call Inbox.
 *
 * One surface. Foreman opens this in the field, sees every revision /
 * finding / safety item that needs them, and closes the loop with two
 * taps max.
 *
 * Statuses (construction language only):
 *   New · Viewed · Acknowledged · Needs Clarification · Field Conflict · Complete
 *
 * Reuses (does not rebuild):
 *   - existing /acks/{id} legacy endpoints stay where they are
 *   - existing AckPill / AckSummary
 *   - SendToForeman component
 *
 * Performance budgets: open < 1000ms, detail < 500ms, acknowledge < 250ms.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Inbox, CheckCircle2, MessageSquareWarning, AlertTriangle,
  Send, Eye, Sparkles, Filter, Loader2, ChevronRight, ShieldAlert, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";
import ImpactChip from "@/components/war_room/ImpactChip";
import StatusPill from "@/components/inbox/StatusPill";
import InboxItemDetail from "@/components/inbox/InboxItemDetail";


export default function ForemanInbox() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filterParam = params.get("status") || "open";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(params.get("id") || null);
  const tOpen = useRef(performance.now());
  const [openMs, setOpenMs] = useState(null);
  const openMsSetRef = useRef(false);

  const reload = useCallback(async () => {
    setLoading(true);
    const tStart = performance.now();
    try {
      const r = await api.get("/foreman/inbox", { params: { include_complete: filterParam === "complete" } });
      setData(r.data);
      if (!openMsSetRef.current) {
        openMsSetRef.current = true;
        setOpenMs(Math.round(performance.now() - tOpen.current));
      }
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Inbox failed to load.");
    } finally {
      setLoading(false);
    }
  }, [filterParam]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const items = data?.items || [];
    if (filterParam === "open") {
      return items.filter((i) => i.status !== "complete");
    }
    if (filterParam === "complete") {
      return items.filter((i) => i.status === "complete");
    }
    return items.filter((i) => i.status === filterParam);
  }, [data, filterParam]);

  const counts = data?.counts || {};
  const active = (data?.items || []).find((i) => i.id === activeId);

  return (
    <div className="h-screen flex flex-col bg-[var(--fop-bg)]" data-testid="foreman-inbox">
      <header className="border-b border-[var(--fop-border)] bg-[var(--fop-surface)] px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
          data-testid="foreman-inbox-back"
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </button>
        <Inbox size={18} className="text-[var(--fop-primary)]" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
            Foreman Roll-Call
          </div>
          <div className="text-sm font-semibold text-[var(--fop-text)]" data-testid="foreman-inbox-headline">
            {data?.user_id ? "Your inbox" : "Inbox"}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="foreman-inbox-tabs">
          <Tab id="open"               label="Open"             count={(counts.new || 0) + (counts.viewed || 0)} current={filterParam} setParams={setParams} />
          <Tab id="new"                label="New"              count={counts.new || 0} current={filterParam} setParams={setParams} />
          <Tab id="acknowledged"       label="Acknowledged"     count={counts.acknowledged || 0} current={filterParam} setParams={setParams} />
          <Tab id="needs_clarification" label="Clarifications"  count={counts.needs_clarification || 0} current={filterParam} setParams={setParams} />
          <Tab id="field_conflict"     label="Conflicts"        count={counts.field_conflict || 0} current={filterParam} setParams={setParams} />
          <Tab id="complete"           label="Complete"         count={counts.complete || 0} current={filterParam} setParams={setParams} />
          {openMs != null && (
            <span
              className="ml-2 text-[var(--fop-text-3)] cursor-default inline-flex"
              data-testid="foreman-inbox-perf"
              title={`Inbox open ${openMs}ms`}
            >
              <Clock size={11} />
            </span>
          )}
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[420px_1fr]">
        <aside
          className={`border-r border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-y-auto ${active ? "hidden lg:block" : ""}`}
          data-testid="foreman-inbox-list"
        >
          {loading && !data ? (
            <div className="p-6 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="foreman-inbox-loading">
              <Loader2 className="animate-spin" size={14} /> Loading the roll call…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center" data-testid="foreman-inbox-empty">
              <CheckCircle2 size={28} className="mx-auto text-[var(--fop-success)]" />
              <div className="mt-2 font-semibold text-[var(--fop-text)]">Nothing waiting</div>
              <div className="mt-1 text-xs text-[var(--fop-text-3)]">All clear in this view.</div>
            </div>
          ) : (
            filtered.map((it) => (
              <InboxRow
                key={it.id}
                item={it}
                active={activeId === it.id}
                onClick={() => { setActiveId(it.id); setParams({ status: filterParam, id: it.id }); }}
              />
            ))
          )}
        </aside>

        <main className={`overflow-y-auto bg-[var(--fop-bg)] ${!active ? "hidden lg:block" : ""}`}>
          {!active ? (
            <div className="h-full flex items-center justify-center text-sm text-[var(--fop-text-3)]" data-testid="foreman-inbox-no-active">
              {filtered.length ? "Pick an item to act on it." : ""}
            </div>
          ) : (
            <InboxItemDetail
              key={active.id}
              itemId={active.id}
              onChanged={() => reload()}
              onClose={() => { setActiveId(null); setParams({ status: filterParam }); }}
            />
          )}
        </main>
      </div>
    </div>
  );
}


function Tab({ id, label, count, current, setParams }) {
  const isActive = current === id;
  return (
    <button
      onClick={() => setParams({ status: id })}
      data-testid={`foreman-inbox-tab-${id}`}
      data-active={isActive ? "1" : "0"}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded text-[10px] font-mono uppercase tracking-wider border min-h-[44px] ${
        isActive
          ? "border-[var(--fop-primary)]/60 bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
          : "border-transparent text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:border-[var(--fop-border)]"
      }`}
    >
      {label}
      <span className={`text-[10px] ${isActive ? "text-[var(--fop-primary)]" : "text-[var(--fop-text-3)]"}`}>
        {count}
      </span>
    </button>
  );
}


function InboxRow({ item, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`foreman-inbox-row-${item.id}`}
      data-status={item.status}
      data-active={active ? "1" : "0"}
      className={`w-full text-left px-3 py-3 border-b border-[var(--fop-border)] flex items-start gap-2 transition-colors ${
        active ? "bg-[var(--fop-primary-soft)]" : "hover:bg-[var(--fop-surface-2)]"
      }`}
    >
      <StatusPill status={item.status} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13px] text-[var(--fop-text)] truncate">
          {item.title || `${item.sheet_number} REV ${item.current_revision}`}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
          {item.sheet_number} · REV {item.current_revision}
          {item.sender_name ? ` · from ${item.sender_name}` : ""}
        </div>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        <ImpactChip severity={item.priority || "moderate"} testid={`foreman-inbox-row-${item.id}-chip`} />
        <ChevronRight size={12} className="text-[var(--fop-text-3)]" />
      </div>
    </button>
  );
}
